import type { ApiError } from '@kia/shared';
import { api, type ApiResult } from './client';
import type { ConfirmOptions } from '../ui';

type ConfirmFn = (o: ConfirmOptions) => Promise<boolean>;

/**
 * POST a price write while honoring the server's write-time magnitude guard (Theme B). If the write is refused
 * with `details.confirmRequired` — a change beyond the confirm band, i.e. a suspected fat-finger extra zero or
 * an OCR toman↔rial 10× — ask the user with the actual jump %, and on confirm resubmit ONCE with
 * `confirmLargeChange`. Returns the final result (the original refusal if the user declines). Every price-write
 * screen (group, material, proof-adopt) shares this so the guard is honored identically.
 */
export async function postPrice<T = unknown>(
  path: string,
  payload: Record<string, unknown>,
  confirm: ConfirmFn,
): Promise<ApiResult<T>> {
  const first = await api<T>(path, { method: 'POST', body: JSON.stringify(payload) });
  const details = (first.body as ApiError | undefined)?.details as
    { confirmRequired?: boolean; jumpPct?: number } | undefined;
  if (first.ok || !details?.confirmRequired) return first;
  const pct = Math.round((details.jumpPct ?? 0) * 100);
  const ok = await confirm({
    title: 'تغییر بزرگ قیمت',
    message: `قیمت جدید حدود ${pct}٪ با قیمت فعلی فاصله دارد — احتمال اشتباه تایپی. با این حساب اعمال شود؟`,
    confirmLabel: 'اعمال',
    danger: true,
  });
  if (!ok) return first;
  return api<T>(path, {
    method: 'POST',
    body: JSON.stringify({ ...payload, confirmLargeChange: true }),
  });
}
